// Catalog data access. Uses Firestore when a backend is configured, otherwise
// falls back to bundled seed data + the in-memory demo store, so the site runs
// as a full clickable demo with zero backend.
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import type { Branch, DailyItem, MenuItem } from "./types";
import { MENU_SEED } from "../data/menu";
import { BRANCHES_SEED } from "../data/branches";
import { demoStore } from "./demoStore";

export const isBackendConfigured = Boolean(import.meta.env.VITE_FIREBASE_API_KEY);

export async function fetchMenuItems(): Promise<MenuItem[]> {
  if (!isBackendConfigured) return MENU_SEED;
  try {
    const snap = await getDocs(
      query(collection(db, "menuItems"), where("active", "==", true)),
    );
    if (snap.empty) return MENU_SEED;
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MenuItem, "id">) }));
  } catch {
    return MENU_SEED;
  }
}

export async function fetchBranches(): Promise<Branch[]> {
  if (!isBackendConfigured) return BRANCHES_SEED;
  try {
    const snap = await getDocs(collection(db, "branches"));
    if (snap.empty) return BRANCHES_SEED;
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Branch, "id">) }));
  } catch {
    return BRANCHES_SEED;
  }
}

/**
 * Live subscription to today's daily-bulk items. Pushes updates as stock changes
 * (real-time sold-out). Returns an unsubscribe function.
 */
export function subscribeDailyItems(
  dateKey: string,
  cb: (items: DailyItem[]) => void,
): () => void {
  if (!isBackendConfigured) {
    return demoStore.subscribeDaily(cb);
  }
  const q = query(
    collection(db, "dailyMenus", dateKey, "items"),
    where("active", "==", true),
  );
  return onSnapshot(
    q,
    (snap) =>
      cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DailyItem, "id">) }))),
    () => cb(demoStore.getDaily()),
  );
}
